import * as React from 'react'

/**
 * Hook to detect if the user is on a device that supports Apple Pay.
 * 
 * This checks for:
 * - iOS devices (iPhone, iPad, iPod)
 * - iPad on iOS 13+ (reports as MacIntel but has touch support)
 * - macOS devices (Mac desktops/laptops)
 * 
 * Apple Pay is available on:
 * - iPhone (iOS)
 * - iPad (iPadOS)
 * - Mac (macOS) - in Safari browser
 * 
 * @returns true if the user is on a device that supports Apple Pay, false otherwise
 */
export const useSupportsApplePay = (): boolean => {
  return React.useMemo(() => {
    // Check for standard iOS devices in user agent
    const isIOSUserAgent = /iPad|iPhone|iPod/.test(navigator.userAgent)
    
    // Check for iPad on iOS 13+ (reports as MacIntel but has touch support)
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
    
    // Check for macOS (Mac desktop/laptop)
    // MacIntel without touch points indicates a Mac (not iPad)
    const isMacOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints === 0
    
    // Also check for Mac using userAgent as a fallback
    const isMacUserAgent = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.userAgent)
    
    return isIOSUserAgent || isIPadOS || isMacOS || isMacUserAgent
  }, [])
}

